import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Empty, Form, Image, Input, Modal, Popconfirm, Row, Space, Spin, Typography, Upload, message } from "antd";
import { CopyOutlined, DeleteOutlined, EditOutlined, ExportOutlined, PictureOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { API_BASE } from "../../config/api";
import { getAuthHeaders } from "../../utils/auth";

const { Title, Text } = Typography;
const publicLink = (id) => `${window.location.origin}/catalogo/${id}`;

export default function PublicImages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/public-images`, { headers: getAuthHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "No se pudieron cargar las imágenes");
      setItems(data);
    } catch (error) { message.error(error.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const edit = (item = null) => {
    setEditing(item);
    setFiles([]);
    form.setFieldsValue({ title: item?.title || "" });
    setOpen(true);
  };
  const save = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    if (!editing && !files.length) return message.warning("Seleccioná una imagen para publicar");
    setSaving(true);
    try {
      const body = new FormData();
      body.append("title", values.title.trim());
      if (files[0]?.originFileObj) body.append("image", files[0].originFileObj);
      const response = await fetch(`${API_BASE}/admin/public-images${editing ? `/${editing.id}` : ""}`, {
        method: editing ? "PUT" : "POST", headers: getAuthHeaders(), body,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "No se pudo publicar la imagen");
      message.success(editing ? "Publicación actualizada. El enlace sigue siendo el mismo." : "Imagen publicada. Ya podés compartir el enlace.");
      setOpen(false);
      await load();
    } catch (error) { message.error(error.message); }
    finally { setSaving(false); }
  };
  const remove = async (item) => {
    try {
      const response = await fetch(`${API_BASE}/admin/public-images/${item.id}`, { method: "DELETE", headers: getAuthHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "No se pudo eliminar");
      message.success("Publicación eliminada");
      await load();
    } catch (error) { message.error(error.message); }
  };
  const copy = async (id) => {
    try { await navigator.clipboard.writeText(publicLink(id)); message.success("Enlace copiado"); }
    catch { message.info("Seleccioná y copiá el enlace que aparece debajo de la imagen."); }
  };

  return (
    <Card style={{ borderRadius: 20, borderColor: "#ece3d3" }}>
      <Row justify="space-between" align="middle" gutter={[16, 16]}>
        <Col><Text style={{ color: "#8a6d3b", fontSize: 11, fontWeight: 800, letterSpacing: 1.5 }}>CONTENIDO PARA TUS CLIENTES</Text><Title level={2} style={{ margin: "6px 0" }}><PictureOutlined /> Catálogo</Title><Text type="secondary">Publicá tu catálogo de servicios, listas de precios o promociones y compartilos por enlace.</Text></Col>
        <Col><Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => edit()} style={{ background: "#8a6d3b" }}>Subir imagen</Button></Col>
      </Row>
      <Space wrap style={{ marginTop: 20 }}>
        <Button icon={<ExportOutlined />} href="/catalogo" target="_blank" rel="noopener noreferrer">Ver catálogo completo</Button>
        <Button icon={<CopyOutlined />} onClick={async () => {
          try { await navigator.clipboard.writeText(`${window.location.origin}/catalogo`); message.success("Enlace del catálogo copiado"); }
          catch { message.info(`Enlace del catálogo: ${window.location.origin}/catalogo`); }
        }}>Copiar enlace del catálogo</Button>
      </Space>
      <Alert style={{ margin: "22px 0", background: "#fffbf2", borderColor: "#ead9b4" }} showIcon type="info" message="Todas tus imágenes aparecen en el catálogo público" description="Compartí el enlace del catálogo para que tus clientes vean todas las imágenes sin iniciar sesión. También podés compartir cada publicación por separado." />
      {loading ? <div style={{ textAlign: "center", padding: 50 }}><Spin /></div> : items.length ? (
        <Row gutter={[20, 20]}>
          {items.map((item) => (
            <Col xs={24} md={12} xl={8} key={item.id}>
              <Card style={{ borderRadius: 16, overflow: "hidden", height: "100%" }}>
                <div style={{ background: "#f6f3ed", textAlign: "center", borderRadius: 10, padding: 8 }}><Image src={item.imageUrl} alt={item.title} height={230} style={{ objectFit: "contain", maxWidth: "100%" }} /></div>
                <Title level={4} style={{ marginBottom: 10, overflowWrap: "anywhere" }}>{item.title}</Title>
                <Input readOnly value={publicLink(item.id)} aria-label={`Enlace público de ${item.title}`} onFocus={(event) => event.target.select()} />
                <Space wrap style={{ marginTop: 14 }}>
                  <Button icon={<CopyOutlined />} onClick={() => copy(item.id)}>Copiar enlace</Button>
                  <Button icon={<ExportOutlined />} href={`/catalogo/${item.id}`} target="_blank" rel="noopener noreferrer">Ver catálogo</Button>
                  <Button icon={<EditOutlined />} onClick={() => edit(item)}>Editar</Button>
                  <Popconfirm title="¿Eliminar esta publicación?" description="Su página pública dejará de estar disponible." onConfirm={() => remove(item)} okText="Eliminar" cancelText="Cancelar"><Button danger icon={<DeleteOutlined />}>Eliminar</Button></Popconfirm>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      ) : <Empty description="Todavía no hay imágenes publicadas. Subí tu primera lista de precios o promoción." />}
      <Modal title={editing ? "Editar publicación" : "Publicar imagen"} open={open} onCancel={() => !saving && setOpen(false)} onOk={save} confirmLoading={saving} closable={!saving} maskClosable={!saving} cancelButtonProps={{ disabled: saving }} okText={editing ? "Guardar cambios" : "Publicar"} cancelText="Cancelar">
        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item label="Título" name="title" rules={[{ required: true, whitespace: true, message: "Ingresá un título" }, { max: 160, message: "Máximo 160 caracteres" }]}><Input placeholder="Ej.: Lista de precios y servicios" maxLength={160} /></Form.Item>
          <Form.Item label="Imagen" extra="JPG, PNG o WebP. Máximo 8 MB. Se muestra completa, sin recortar.">
            <Upload accept="image/jpeg,image/png,image/webp" maxCount={1} fileList={files} listType="picture" beforeUpload={(file) => {
              if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 8 * 1024 * 1024) { message.error("Elegí una imagen JPG, PNG o WebP de hasta 8 MB"); return Upload.LIST_IGNORE; }
              return false;
            }} onChange={({ fileList }) => setFiles(fileList.slice(-1))}><Button icon={<UploadOutlined />}>{editing ? "Reemplazar imagen" : "Seleccionar imagen"}</Button></Upload>
            {editing && !files.length && <Image src={editing.imageUrl} alt={editing.title} height={100} style={{ marginTop: 12, objectFit: "contain" }} />}
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
